import { FC, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Outlet, Route, HashRouter as Router, Routes, useLocation, useParams } from 'react-router-dom';
import { CasumoLoaderIcon } from './assets/icons';
import { getFlowConfig, TCmsFlow } from './cms';
import PnpCashier from './components/pnpCashier/PnpCashier';
import type { InitializeDepositRequest } from './components/pnpCashier/pnpCashier.types';
import { pnpRetryStore } from './components/pnpCashier/pnpRetryStore';
import TrustlyIframe from './components/trustlyIframe/TrustlyIframe';
import { isTrustlyCompleted } from './components/trustlyIframe/trustlyUtils';
import Flow from './flow/flow';
import { FlowProvider } from './flow/flowContext';
import FlowDialog from './flow/flowDialog';
import useMarketInfo from './hooks/useMarketInfo';
import useTransformData from './hooks/useTransformData';
import RouteGuard from './routes/routeGuard';
import { useRegistrationStore } from './store/store';
import { FLOW_NAMES } from './utils/countries';
import { IS_DEVELOPMENT } from './utils/envConstants';
import { gatekeeper } from './utils/gatekeeper';
import { GtmEvents } from './utils/gtm/gtm.ts';
import { initiateNativeRegistration } from './utils/native';
import { clearPendingRegisterStart, hasPendingRegisterStart } from './utils/persistedState';
import { timerStart } from './utils/timer';
import { filterFlowByUserLocation } from './utils/utils.ts';

export const DialogVisibilityHandler: FC = () => {
  const location = useLocation();

  const setMetadata = useRegistrationStore(state => state.setMetadata);
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    if (!IS_DEVELOPMENT) {
      const params = new URLSearchParams(window?.location?.search);

      const welcomeOfferName = params?.get('exc_wo');

      if (welcomeOfferName) {
        sessionStorage.setItem('fabric:registration:affiliateWelcomeOffer', welcomeOfferName);
      }

      if (sessionStorage.getItem('fabric:registration:suppressAutoOpen') === 'true') {
        sessionStorage.removeItem('fabric:registration:suppressAutoOpen');
      } else if (
        gatekeeper.navigation.isActiveRoute('/register', {}) ||
        location.hash === '#signup' ||
        sessionStorage.getItem('fabric:registration:open') === 'true'
      ) {
        setMetadata({ isOpen: true });
      }

      // Sign Up clicked before we subscribed: the 'start' message was dropped,
      // so replay it from spine's backup flag.
      if (hasPendingRegisterStart()) {
        clearPendingRegisterStart();
        initiateNativeRegistration().then(() => {
          setMetadata({ isOpen: true });
        });
      }

      const unsubscribe = gatekeeper.messageBus.subscribe(
        'registration',
        async ({ kind, payAndPlay, payload }: {
          kind: string;
          payAndPlay?: { url?: string };
          payload?: InitializeDepositRequest;
        }) => {
          if (kind === 'start') {
            // Spine also sets the backup flag on live clicks; consume it so a
            // later remount doesn't reopen the modal.
            clearPendingRegisterStart();
            await initiateNativeRegistration();
            setMetadata({
              isOpen:             true,
              trustlyCheckoutUrl: payAndPlay?.url,
            });
          }

          if (kind === 'error') {
            if (payload) pnpRetryStore.set(payload);
            setMetadata({ isOpen: true });
            const err = new Error('pnp_init_failed');
            err.name = 'pnp_init_failed';
            showBoundary(err);
          }
        },
      ) as unknown as () => void;

      return () => {
        unsubscribe();
      };
    }
  }, [setMetadata, showBoundary]);

  return null;
};

const DialogRenderer: FC = () => {
  const isOpen = useRegistrationStore(state => state.metadata?.isOpen);

  if (!IS_DEVELOPMENT && !isOpen) {
    return null;
  }

  return (
    <FlowDialog>
      <Outlet />
    </FlowDialog>
  );
};

const RegistrationFlow: FC = () => {
  const { step } = useParams<{
    market: string;
    step: string;
  }>();
  const { transformData } = useTransformData();
  const { formData, metadata } = useRegistrationStore();
  const { language, flowName, market } = useMarketInfo();

  const [rawFlow, setRawFlow] = useState<TCmsFlow>();
  const [loading, setLoading] = useState(true);

  const isPnpFlow = flowName === FLOW_NAMES.MGA_GRA_PNP;
  const [trustlyCompleted, setTrustlyCompleted] = useState(() =>
    isPnpFlow ? isTrustlyCompleted() : false,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getFlowConfig({ flowName, language }).then((flow) => {
      if (cancelled) {
        return;
      }

      setRawFlow(flow as TCmsFlow | undefined);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [flowName, language]);

  const config = useMemo(
    () => (rawFlow ? transformData(filterFlowByUserLocation(rawFlow)) : undefined),
    // re-run mapping (and {{variable}} interpolation) when live form state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawFlow, formData, metadata],
  );

  useEffect(() => {
    if (!IS_DEVELOPMENT) {
      GtmEvents.logRegistrationStart();
      timerStart();
    }
  }, []);

  if (loading) {
    return <div className="animate-spin rounded-md">
      <CasumoLoaderIcon width={64} height={64} />
    </div>;
  }

  // Show PnP pre-flow: cashier (if no URL yet) then Trustly iframe
  if (isPnpFlow && !trustlyCompleted) {
    if (!metadata?.trustlyCheckoutUrl) {
      return <PnpCashier onSkip={() => setTrustlyCompleted(true)} />;
    }

    return <TrustlyIframe onComplete={() => setTrustlyCompleted(true)} />;
  }

  return config ? (
    <RouteGuard market={market} validMarket={config} step={step}>
      <FlowProvider market={market} config={config}>
        <Flow />
      </FlowProvider>
    </RouteGuard>
  ) : null;
};

const App: FC = () => (
  <Router>
    <DialogVisibilityHandler />
    <Routes>
      <Route element={<DialogRenderer />}>
        <Route
          path={!IS_DEVELOPMENT ? '/:step' : '/:market/:step'}
          element={<RegistrationFlow />}
        />
        <Route
          path={!IS_DEVELOPMENT ? '/' : '/:market'}
          element={<RegistrationFlow />}
        />
        <Route path="/" element={<RegistrationFlow />} />
      </Route>
    </Routes>
  </Router>
);

export default App;
